import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
    ArrowLeft, ArrowRight, Loader2, Check, Plus, Trash2,
    BookOpen, AlertCircle, Sparkles, Info, Calendar, Clock
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyEpisodio = {
    fecha_hora: '',
    tarea: '',
    contexto: '',
    emocion_pensamiento: '',
    conducta_sustituta: '',
    tiempo_invertido: '',
    costo_inmediato: '',
    costo_posterior: ''
};

export default function Modulo2Episodios() {
    const navigate = useNavigate();
    const [episodios, setEpisodios] = useState([
        { ...emptyEpisodio },
        { ...emptyEpisodio },
        { ...emptyEpisodio },
        { ...emptyEpisodio },
        { ...emptyEpisodio }
    ]);
    const [currentEpisode, setCurrentEpisode] = useState(0);
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');

    // Load draft on mount
    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/registro_episodios`, {
                    withCredentials: true
                });
                if (data.data?.episodios) {
                    // Ensure we always have at least 5 episodes
                    const loaded = data.data.episodios;
                    while (loaded.length < 5) {
                        loaded.push({ ...emptyEpisodio });
                    }
                    setEpisodios(loaded);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch (error) {
                // silent - draft not found is expected
            } finally {
                setLoadingDraft(false);
            }
        };
        loadDraft();
    }, []);

    // Auto-save draft
    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(
                `${API_URL}/api/user/draft/save`,
                { module_id: 'registro_episodios', data: { episodios } },
                { withCredentials: true }
            );
        } catch (error) {
            // silent - non-critical draft save
        } finally {
            setSavingDraft(false);
        }
    }, [episodios]);

    // Debounced auto-save
    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [episodios, saveDraft, loadingDraft, readOnly]);

    const updateEpisodio = (index, field, value) => {
        setEpisodios(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const isEpisodioComplete = (ep) => {
        return ep.fecha_hora.trim().length > 0 &&
               ep.tarea.trim().length > 5 &&
               ep.contexto.trim().length > 0 &&
               ep.emocion_pensamiento.trim().length > 0 &&
               ep.conducta_sustituta.trim().length > 0 &&
               ep.tiempo_invertido.trim().length > 0 &&
               ep.costo_inmediato.trim().length > 0 &&
               ep.costo_posterior.trim().length > 0;
    };

    const completedCount = episodios.filter(isEpisodioComplete).length;
    
    // Check for substitute behavior in at least 3 entries
    const withSubstitute = episodios.filter(ep => ep.conducta_sustituta.trim().length > 0).length;
    const hasEnoughSubstitute = withSubstitute >= 3;

    const canSubmit = !readOnly && (completedCount >= 5 && hasEnoughSubstitute);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) {
            toast.error('Completa los 5 episodios con conducta sustituta en al menos 3');
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/user/modules/episodios/submit`,
                { episodios },
                { withCredentials: true }
            );
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('Módulo completado exitosamente');
        } catch (error) {
            const message = error.response?.data?.detail || 'Error al enviar el módulo';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (loadingDraft) {
        return (
            <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#425A65]" />
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl senda-card animate-fade-in-up">
                    <CardContent className="pt-8 text-center">
                        <div className="w-20 h-20 bg-[#7F9C8B] rounded-full flex items-center justify-center mx-auto mb-6">
                            <Check className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="senda-heading-2 mb-4">¡Excelente trabajo!</h2>
                        
                        {aiInsight && (
                            <div className="senda-ai-panel text-left mb-6">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-[#A29DB0] shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-xs font-semibold text-[#A29DB0] uppercase mb-2">Reflexión para ti</p>
                                        <p className="text-[#1F2933] italic">"{aiInsight}"</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <p className="senda-body mb-6">
                            Has completado el registro de tus episodios de evitación. 
                            Esta información es valiosa para entender tus patrones de comportamiento.
                            Tu mentor-psicóloga te proporcionará retroalimentación personalizada.
                        </p>
                        
                        <div className="mb-6"><ModuleFeedback moduleId="registro_episodios" chapter={1} /></div>
                        <Link to="/usuario">
                            <Button className="senda-btn-primary" data-testid="back-to-dashboard-btn-episodios">
                                Volver al inicio
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const currentEp = episodios[currentEpisode];

    return (
        <div className="min-h-screen bg-[#FBFBF9]">
            <Toaster position="top-right" richColors />
            
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario">
                                <Button variant="ghost" size="icon" className="text-[#425A65]" data-testid="back-to-usuario-episodios">
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                            </Link>
                            <div>
                                <h1 className="text-lg font-semibold text-[#1F2933] font-['Outfit']">Registro de episodios</h1>
                                <p className="text-sm text-[#616E7C]">Episodio {currentEpisode + 1} de 5</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {savingDraft && !readOnly && (
                                <span className="text-xs text-[#9AA5B1] flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    Guardando...
                                </span>
                            )}
                            <Badge className={completedCount >= 5 ? 'senda-status-success' : 'bg-[#EAE8E3] text-[#616E7C]'}>
                                {completedCount}/5 completos
                            </Badge>
                        </div>
                    </div>
                </div>
            </header>

            {/* Progress indicators */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex gap-2">
                    {[0, 1, 2, 3, 4].map((idx) => (
                        <button
                            key={idx}
                            onClick={() => setCurrentEpisode(idx)}
                            className={`flex-1 h-2 rounded-full transition-all ${
                                idx === currentEpisode 
                                    ? 'bg-[#425A65]' 
                                    : isEpisodioComplete(episodios[idx])
                                        ? 'bg-[#7F9C8B]'
                                        : 'bg-[#EAE8E3]'
                            }`}
                            data-testid={`progress-episodio-${idx}`}
                        />
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="senda-module-container animate-fade-in-up">
                    <CardHeader className="pb-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-[#425A65] rounded-xl flex items-center justify-center">
                                <BookOpen className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#425A65]">Episodio {currentEpisode + 1}</CardTitle>
                                <p className="text-sm text-[#616E7C]">Registra un episodio de evitación</p>
                            </div>
                        </div>
                        
                        {/* Info box */}
                        <div className="p-4 bg-[#FBFBF9] rounded-lg border border-[#EAE8E3] flex items-start gap-3">
                            <Info className="h-5 w-5 text-[#A29DB0] shrink-0 mt-0.5" />
                            <p className="text-sm text-[#616E7C]">
                                Piensa en un momento específico en que evitaste una tarea. 
                                Registra todos los detalles para identificar patrones en tu comportamiento.
                            </p>
                        </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-6">
                        {/* Fecha y hora */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="senda-label flex items-center gap-2">
                                    <Calendar className="h-4 w-4" />
                                    Fecha y hora
                                </Label>
                                <Input
                                    type="datetime-local"
                                    value={currentEp.fecha_hora}
                                    onChange={(e) => updateEpisodio(currentEpisode, 'fecha_hora', e.target.value)}
                                    className="senda-input"
                                    data-testid={`fecha-hora-input-${currentEpisode}`}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="senda-label flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    Tiempo invertido en la conducta sustituta
                                </Label>
                                <Input
                                    value={currentEp.tiempo_invertido}
                                    onChange={(e) => updateEpisodio(currentEpisode, 'tiempo_invertido', e.target.value)}
                                    placeholder="Ej: 2 horas, 30 minutos"
                                    className="senda-input"
                                    data-testid={`tiempo-input-${currentEpisode}`}
                                />
                            </div>
                        </div>

                        {/* Tarea */}
                        <div className="space-y-2">
                            <Label className="senda-label">¿Qué tarea estabas evitando?</Label>
                            <Input
                                value={currentEp.tarea}
                                onChange={(e) => updateEpisodio(currentEpisode, 'tarea', e.target.value)}
                                placeholder="Describe la tarea específica que debías hacer"
                                className="senda-input"
                                data-testid={`tarea-input-${currentEpisode}`}
                            />
                            <p className="text-xs text-[#9AA5B1]">Sé específico/a. Evita respuestas vagas como "trabajo" o "estudiar".</p>
                        </div>

                        {/* Contexto */}
                        <div className="space-y-2">
                            <Label className="senda-label">Contexto (dónde estabas, qué pasaba alrededor)</Label>
                            <Input
                                value={currentEp.contexto}
                                onChange={(e) => updateEpisodio(currentEpisode, 'contexto', e.target.value)}
                                placeholder="Ej: En casa, solo/a, después de comer..."
                                className="senda-input"
                                data-testid={`contexto-input-${currentEpisode}`}
                            />
                        </div>

                        {/* Emoción o pensamiento inicial */}
                        <div className="space-y-2">
                            <Label className="senda-label">¿Qué emoción o pensamiento tuviste antes de evitar?</Label>
                            <Textarea
                                value={currentEp.emocion_pensamiento}
                                onChange={(e) => updateEpisodio(currentEpisode, 'emocion_pensamiento', e.target.value)}
                                placeholder='Ej: Sentí ansiedad, pensé "esto es demasiado difícil"...'
                                className="senda-input min-h-[80px]"
                                data-testid={`emocion-input-${currentEpisode}`}
                            />
                        </div>

                        {/* Conducta sustituta */}
                        <div className="space-y-2">
                            <Label className="senda-label">¿Qué hiciste en lugar de la tarea? (conducta sustituta)</Label>
                            <Textarea
                                value={currentEp.conducta_sustituta}
                                onChange={(e) => updateEpisodio(currentEpisode, 'conducta_sustituta', e.target.value)}
                                placeholder="Ej: Vi videos en YouTube, revisé redes sociales, limpié la casa..."
                                className="senda-input min-h-[80px]"
                                data-testid={`conducta-input-${currentEpisode}`}
                            />
                        </div>

                        {/* Costos */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="senda-label">Costo inmediato (qué perdiste en el momento)</Label>
                                <Input
                                    value={currentEp.costo_inmediato}
                                    onChange={(e) => updateEpisodio(currentEpisode, 'costo_inmediato', e.target.value)}
                                    placeholder="Ej: Tiempo, energía, oportunidad..."
                                    className="senda-input"
                                    data-testid={`costo-inmediato-input-${currentEpisode}`}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="senda-label">Costo posterior (consecuencias después)</Label>
                                <Input
                                    value={currentEp.costo_posterior}
                                    onChange={(e) => updateEpisodio(currentEpisode, 'costo_posterior', e.target.value)}
                                    placeholder="Ej: Estrés, culpa, trabajo acumulado..."
                                    className="senda-input"
                                    data-testid={`costo-posterior-input-${currentEpisode}`}
                                />
                            </div>
                        </div>

                        {/* Validation message */}
                        {completedCount >= 5 && !hasEnoughSubstitute && (
                            <div className="p-4 bg-[#E8B27A]/10 rounded-lg border border-[#E8B27A]/20 flex items-start gap-3">
                                <AlertCircle className="h-5 w-5 text-[#E8B27A] shrink-0 mt-0.5" />
                                <p className="text-sm text-[#E8B27A]">
                                    Necesitas describir la conducta sustituta en al menos 3 episodios para completar el módulo.
                                </p>
                            </div>
                        )}

                        {/* Navigation */}
                        <div className="flex items-center justify-between pt-6 border-t border-[#EAE8E3]">
                            <Button
                                variant="ghost"
                                onClick={() => setCurrentEpisode(prev => Math.max(0, prev - 1))}
                                disabled={currentEpisode === 0}
                                className="senda-btn-ghost"
                                data-testid="prev-episodio-btn"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Anterior
                            </Button>
                            
                            {currentEpisode < 4 ? (
                                <Button
                                    onClick={() => setCurrentEpisode(prev => Math.min(4, prev + 1))}
                                    className="senda-btn-primary"
                                    data-testid="next-episodio-btn"
                                >
                                    Siguiente
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!canSubmit || loading}
                                    className="senda-btn-primary"
                                    data-testid="submit-episodios-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Enviando...
                                        </>
                                    ) : (
                                        <>
                                            Completar módulo
                                            <Check className="h-4 w-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
